import {Button, Flex, SnackbarService} from "@hps/hops-react";
import {CheckoutDiscountService} from "@hps/hops-sdk-js";
import Typography from "@mui/material/Typography";
import {useCallback, useRef, useState} from "react";

import Link from "Components/Link";
import String from "Components/String";
import TextField from "Components/TextField";
import dCheckoutBasketDiscounts from "Dispatchers/dCheckoutBasketDiscounts.js";
import dCheckoutBasketLoading from "Dispatchers/dCheckoutBasketLoading.js";
import useCheckoutBasket from "Hooks/useCheckoutBasket";
import useFeatures from "Hooks/useFeatures";
import useOrg from "Hooks/useOrg";
import Routes from "Resources/Routes.json";

import scss from "./CheckoutDiscountsUiInput.module.scss";

const CheckoutDiscountsUiInput = () => {

	const {org} = useOrg();
	const {checkoutBasketId, checkoutBasketDiscounts, checkoutBasketLoading} = useCheckoutBasket();

	const {isFeatureEnabled} = useFeatures();

	const inputRef = useRef();
	const [inputValue, setInputValue] = useState(null);
	const [isSubmitting, setIsSubmitting] = useState(false);


	/**
	 * Handle a submission error.
	 *
	 * @param {String} msg
	 * @return {void}
	 */
	const handleError = useCallback(msg => {
		inputRef?.current?.select?.();
		SnackbarService.snack(msg, "error");
	}, [inputRef]);


	/**
	 * Input value changed.
	 *
	 * @param {String|null} value
	 * @return {void}
	 */
	const handleInputValueChange = useCallback(value => {
		setInputValue((value?.toUpperCase() || null));
	}, []);


	/**
	 * Changing whether we are in the submitting state.
	 *
	 * @param {Boolean} submitting
	 * @return {void}
	 */
	const handleIsSubmittingChange = useCallback(submitting => {
		setIsSubmitting(submitting);
		dCheckoutBasketLoading(submitting);
	}, []);


	/**
	 * Submit a request to apply a discount code.
	 *
	 * @async
	 * @param {FormEvent} e
	 * @return {void}
	 */
	const handleApplySubmit = useCallback(async e => {

		e.preventDefault();

		if (checkoutBasketLoading) {
			return;
		}

		/**
		 * Discount code
		 */
		const code = inputValue;

		/**
		 * Check the user hasn't already applied this code
		 */
		if (checkoutBasketDiscounts.find(discount => (discount.Discount.Code === code))) {
			handleError("You've already applied this discount code.");
			return;
		}

		handleIsSubmittingChange(true);

		/**
		 * We are going to try to apply this discount code to the basket
		 */
		try {

			/**
			 * Make the API call to apply the discount code
			 */
			const appliedBasketDiscounts = await CheckoutDiscountService.applyDiscountCode(
				checkoutBasketId,
				code,
				org.Id
			);

			/**
			 * Update the discounts in our basket to match what the server says
			 */
			dCheckoutBasketDiscounts(appliedBasketDiscounts);

			/**
			 * Did we successfully apply the discount code?
			 */
			if (appliedBasketDiscounts.find(d => (d.Discount.Code === code))) {
				setInputValue(null);
				SnackbarService.snack("Your discount has been applied.", "success");
			}
			else {
				handleError(`Discount code "${code}" cannot be applied to any of the items in your basket.`);
			}

		}

		/**
		 * Something went wrong
		 */
		catch (e) {
			switch (e?.response?.data) {
				case "E_DISCOUNT_INVALID":
					handleError("This Discount code is invalid. Please check you've typed it correctly.");
					break;
				case "E_DISCOUNT_EXPIRED":
					handleError("This Discount code has expired.");
					break;
				case "E_DISCOUNT_ALREADY_APPLIED":
					handleError("This Discount code has already been applied.");
					break;
				case "E_INVALID_IN_CONTEXT_VOUCHER":
					handleError("The code you entered is a Voucher and can't be used here - please apply it at checkout on the payment page.");
					break;
				case "E_INVALID_IN_CONTEXT_GIFT_CARD":
					handleError("The code you entered is a Gift Card and can't be used here - please apply it on the Redeem Gift Card page.");
					break;
				default:
					handleError(e);
					break;
			}
		}

		handleIsSubmittingChange(false);

	}, [checkoutBasketDiscounts, checkoutBasketId, checkoutBasketLoading, inputValue, org, handleError, handleIsSubmittingChange]);


	/**
	 * Render!
	 */
	return (
		<Flex>
			<Flex gap={0}>
				<String
					noFlex={true}
					str="If you have a DISCOUNT CODE, please enter it here."
					variant="body2"
					color="error" />
				{isFeatureEnabled("VoucherRedemption") ?
					<String
						noFlex={true}
						str="If you have a VOUCHER, please proceed to checkout and enter it there."
						variant="body2"
						color="error" /> :
					null
				}
				{isFeatureEnabled("GiftCardRedemption") ?
					<Typography
						variant="body2"
						color="error">
						If you have a GIFT CARD, please enter it on the <Link variant="body2" uri={`${Routes.giftcards}/redeem`} label="Redeem Gift Card" style={{display: "inline-block"}} /> page.
					</Typography> :
					null
				}
			</Flex>
			<form
				onSubmit={handleApplySubmit}>
				<Flex columnar={true}>
					<Flex columnar={true}>
						<TextField
							className={scss.input}
							color="secondary"
							inputProps={{readOnly: isSubmitting}}
							inputRef={inputRef}
							onChange={handleInputValueChange}
							pattern="[A-Z0-9]{1,}"
							placeholder="Discount Code"
							required={true}
							value={inputValue} />
						<Button
							color="secondary"
							disabled={checkoutBasketLoading}
							label="Apply"
							loading={isSubmitting}
							size="large"
							type="submit" />
					</Flex>
					<Flex></Flex>
				</Flex>
			</form>
		</Flex>
	);

};

export default CheckoutDiscountsUiInput;
